import {
  Box,
  Flex,
  Text,
  Image,
  Spinner,
  useToast,
  Alert,
  Select,
  Button,
  Collapse,
  useMediaQuery,
} from '@chakra-ui/react'
import LabCorpLogo from 'assets/images/LabCorpLogo.png'
import {
  LabSchedulingState,
  useLabSchedulingContext,
  validTimeZones,
} from 'context/LabSchedulingContext/LabSchedulingProvider'
import { Map } from '@vis.gl/react-google-maps'
import { useLabScheduling } from 'hooks/useLabScheduling/useLabscheduling'
import { useState, useMemo, useEffect, useRef } from 'react'
import DayCarousel from './DayCarousel'
import TimeSlotCard from './TimeSlotCard'
import MonthCarousel from './MonthCarousel'
import alertUrl from 'assets/images/alert-info.svg'
import GoogleMarkerWithWindow from 'components/GoogleMarkerWithWindow/GoogleMarkerWithWindow'
import {
  LabSchedulingErrorCode,
  useLabSchedulingError,
} from 'hooks/useLabScheduling/useLabSchedulingError'
import { LabProvider, LabSchedulingFilter } from 'utils/amplitude.types'
import { useLabSchedulingAmplitude } from 'hooks/useLabScheduling/useLabSchedulingAmplitude'
import { RoutePaths } from 'containers/Core/Routes'
import { ChakraPrimaryButton } from 'components/Button'

type TimeSlotMapProps = {
  isMobile: boolean
}

// miles and sorted in ascending order
const DEFAULT_SEARCH_RADIUS = 10
const radiusOptions = [5, DEFAULT_SEARCH_RADIUS, 25, 50, 100].sort(
  (a, b) => a - b
)

const timezoneOptions = [
  validTimeZones.NEW_YORK,
  validTimeZones.CHICAGO,
  validTimeZones.DENVER,
  validTimeZones.LOS_ANGELES,
  validTimeZones.ANCHORAGE,
  validTimeZones.HONOLULU,
]

const cacheDayRange = 7

// Helper function to get date string in local timezone (not UTC)
const getLocalDateString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`
}

// Helper function to check if a timezone is valid
const isValidTimeZone = (timezone: string): timezone is validTimeZones => {
  return Object.values(validTimeZones).includes(timezone as validTimeZones)
}

const TimeSlotMap = ({ isMobile }: TimeSlotMapProps) => {
  const toast = useToast()
  const {
    setCurrentScreen,
    selectedLab,
    labState,
    setLabState,
    isModifyFlow,
    amplitudeLabSchedulingMetadata,
    resetSelectedTime,
  } = useLabSchedulingContext()
  const {
    useCachedTimeSlots,
    filterTimeSlotsByDate,
    getAvailableDaysFromCache,
  } = useLabScheduling()

  const { handleLabSchedulingError } = useLabSchedulingError()
  const [focusedLocationId, setFocusedLocationId] = useState('')
  const [criticalErrorCode, setCriticalErrorCode] = useState('')
  const [sortBy, setSortBy] = useState<'distance' | 'firstAvailable'>(
    'distance'
  )
  const [searchRadius, setSearchRadius] = useState(DEFAULT_SEARCH_RADIUS)
  const [selectedTimezone, setSelectedTimezone] = useState(
    validTimeZones.NEW_YORK
  )

  const today = useMemo(() => new Date(), [])
  const todayString = useMemo(() => getLocalDateString(today), [today])

  // Initialize selected date with first available day
  const [selectedDate, setSelectedDate] = useState('')

  const [selectedMonth, setSelectedMonth] = useState(today)
  const previousCacheStartDateRef = useRef('')
  const [isMapVisible, setIsMapVisible] = useState(true)
  const [areFiltersVisible, setAreFiltersVisible] = useState(false)
  const [isSmallScreen] = useMediaQuery('(max-width: 480px)')

  const cacheStartDate = useMemo(() => {
    if (!selectedDate || !previousCacheStartDateRef.current) {
      return todayString
    }

    const selectedDateObj = new Date(selectedDate + 'T00:00:00')
    const previousCacheStartDateObj = new Date(
      previousCacheStartDateRef.current + 'T00:00:00'
    )

    const daysDiff = Math.floor(
      (selectedDateObj.getTime() - previousCacheStartDateObj.getTime()) /
        (1000 * 60 * 60 * 24)
    )

    const shouldUseSelectedDate = daysDiff >= cacheDayRange || daysDiff < 0

    if (shouldUseSelectedDate) {
      return selectedDate
    } else {
      return previousCacheStartDateRef.current
    }
  }, [selectedDate, todayString])

  // Handle the mutation in a useEffect
  useEffect(() => {
    if (!selectedDate || !previousCacheStartDateRef.current) {
      previousCacheStartDateRef.current = todayString
    } else {
      const selectedDateObj = new Date(selectedDate + 'T00:00:00')
      const previousCacheStartDateObj = new Date(
        previousCacheStartDateRef.current + 'T00:00:00'
      )

      const daysDiff = Math.floor(
        (selectedDateObj.getTime() - previousCacheStartDateObj.getTime()) /
          (1000 * 60 * 60 * 24)
      )

      const shouldUseSelectedDate = daysDiff >= cacheDayRange || daysDiff < 0

      if (shouldUseSelectedDate) {
        previousCacheStartDateRef.current = selectedDate
      }
    }
  }, [selectedDate, todayString])

  const {
    data: cachedTimeSlotsResponse,
    isLoading: isLoadingCachedTimeSlots,
    error: cachedTimeSlotsError,
  } = useCachedTimeSlots(
    selectedLab,
    labState.selectedAddress,
    searchRadius,
    cacheStartDate,
    cacheDayRange
  )

  // Get available days from cached data
  const availableDays = useMemo(() => {
    return getAvailableDaysFromCache(cachedTimeSlotsResponse)
  }, [cachedTimeSlotsResponse, getAvailableDaysFromCache])

  // Get filtered time slots for selected date
  const currentTimeSlotsResponse = useMemo(() => {
    return filterTimeSlotsByDate(
      cachedTimeSlotsResponse,
      selectedDate,
      selectedTimezone
    )
  }, [
    cachedTimeSlotsResponse,
    selectedDate,
    selectedTimezone,
    filterTimeSlotsByDate,
  ])

  useEffect(() => {
    if (cachedTimeSlotsResponse?.locations.length) {
      const timezone = cachedTimeSlotsResponse.locations[0].address.timezone
      setCriticalErrorCode('')

      if (isValidTimeZone(timezone)) {
        handleTimezoneChange(timezone as validTimeZones)
      }
    }
  }, [cachedTimeSlotsResponse])

  const sortedLocationsByDistance = useMemo(() => {
    if (!currentTimeSlotsResponse?.locations) {
      return []
    }

    return [...currentTimeSlotsResponse.locations].sort(
      (a, b) => a.distance - b.distance
    )
  }, [currentTimeSlotsResponse])

  const sortedLocationsByFirstAvailable = useMemo(() => {
    if (!currentTimeSlotsResponse?.locations) {
      return []
    }

    return [...currentTimeSlotsResponse.locations].sort((a, b) => {
      const aFirstTime = a.slots[0]?.time
      const bFirstTime = b.slots[0]?.time
      return aFirstTime.localeCompare(bFirstTime)
    })
  }, [currentTimeSlotsResponse])

  // All days to show in DayCarousel - show all days in month, highlight available ones
  const daysToShow = useMemo(() => {
    const today = new Date()
    const lastDay = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0
    )

    // If selected month is current month, start from today
    const isSameMonthAndYear =
      selectedMonth.getMonth() === today.getMonth() &&
      selectedMonth.getFullYear() === today.getFullYear()
    const startDay = isSameMonthAndYear ? today.getDate() : 1

    const days: Date[] = []
    const lastDayNumber = lastDay.getDate()

    for (let day = startDay; day <= lastDayNumber; day++) {
      const date = new Date(
        selectedMonth.getFullYear(),
        selectedMonth.getMonth(),
        day
      )
      days.push(date)
    }

    return days
  }, [selectedMonth])

  const {
    fireSlotSelectedAmplitudeEvent,
    fireAvailabilityShowAmplitudeEvent,
  } = useLabSchedulingAmplitude({
    selectedLab,
    selectedDate,
    selectedTimezone,
    searchRadius,
    sortBy,
    isModifyFlow,
    amplitudeLabSchedulingMetadata,
    labState,
    cachedTimeSlotsResponse,
    availableDays,
    sortedLocationsByFirstAvailable,
    todayString,
    selectedAddress: labState.selectedAddress,
  })
  // Update selected month when selected date changes
  useEffect(() => {
    if (selectedDate) {
      // Parse the date safely to avoid timezone issues
      // selectedDate is in format "YYYY-MM-DD"
      const [year, month, day] = selectedDate.split('-').map(Number)
      const dateObj = new Date(year, month - 1, day) // month is 0-indexed
      const newMonth = dateObj.getMonth()
      const newYear = dateObj.getFullYear()

      // Only update if the month/year is actually different
      if (
        selectedMonth.getMonth() !== newMonth ||
        selectedMonth.getFullYear() !== newYear
      ) {
        setSelectedMonth(dateObj)
      }
    }
  }, [selectedDate, selectedMonth])

  // Update selected date when available days change
  useEffect(() => {
    if (availableDays.length > 0) {
      // Only update selectedDate if:
      // We don't have a selected date yet (empty string)

      if (!selectedDate) {
        // Try to find an available date in the current month first
        const currentMonth = new Date().getMonth()
        const currentYear = new Date().getFullYear()

        const availableInCurrentMonth = availableDays.find((dateStr) => {
          const [year, month, day] = dateStr.split('-').map(Number)
          const date = new Date(year, month - 1, day) // month is 0-indexed
          return (
            date.getMonth() === currentMonth &&
            date.getFullYear() === currentYear
          )
        })

        const newSelectedDate = availableInCurrentMonth || availableDays[0]
        setSelectedDate(newSelectedDate)
        setLabState({ selectedDate: newSelectedDate })
      }
    }
  }, [availableDays, setLabState])

  // Fire amplitude event when conditions are met
  useEffect(() => {
    if (selectedDate && cachedTimeSlotsResponse?.locations?.length) {
      fireAvailabilityShowAmplitudeEvent()
    }
  }, [
    selectedDate,
    cachedTimeSlotsResponse,
    sortBy,
    searchRadius,
    selectedTimezone,
  ])

  const handleTimezoneChange = (timezone: validTimeZones) => {
    resetSelectedTime()
    setSelectedTimezone(timezone)
    setLabState({ displayTimezone: timezone })
  }

  const handleSearchRadiusChange = (radius: number) => {
    resetSelectedTime()
    setSearchRadius(radius)
  }

  const handleSortByChange = (sortBy: 'distance' | 'firstAvailable') => {
    resetSelectedTime()
    setSortBy(sortBy)
  }

  const handleMonthChange = async (newMonth: Date) => {
    resetSelectedTime()
    setSelectedMonth(newMonth)

    // Find first available day in the new month
    const currentDate = new Date()
    const isCurrentMonthAndYear =
      newMonth.getMonth() === currentDate.getMonth() &&
      newMonth.getFullYear() === currentDate.getFullYear()
    const firstDay = isCurrentMonthAndYear ? currentDate.getDate() : 1

    // Find the first available day in this month
    const firstAvailableInMonth = availableDays.find((dateStr) => {
      // Parse the date safely to avoid timezone issues
      const [year, month, day] = dateStr.split('-').map(Number)
      const date = new Date(year, month - 1, day) // month is 0-indexed
      return (
        date.getMonth() === newMonth.getMonth() &&
        date.getFullYear() === newMonth.getFullYear() &&
        date.getDate() >= firstDay
      )
    })

    if (firstAvailableInMonth) {
      // Parse the selected date safely as well
      const [year, month, day] = firstAvailableInMonth.split('-').map(Number)
      const selectedDate = new Date(year, month - 1, day)
      handleDateSelect(selectedDate)
    } else {
      // If no available days in cache for this month, select the first day
      // This will trigger a new fetch via the cacheStartDate logic
      const firstDayOfMonth = new Date(
        newMonth.getFullYear(),
        newMonth.getMonth(),
        firstDay
      )
      handleDateSelect(firstDayOfMonth)
    }
  }

  const handleDateSelect = (date: Date) => {
    const formattedDate = getLocalDateString(date)
    resetSelectedTime()
    setSelectedDate(formattedDate)
    setLabState({ selectedDate: formattedDate })
  }

  const handleSelectSlot = (
    id: string,
    address: string,
    time: string,
    timezone: string
  ) => {
    const updateObject: Partial<LabSchedulingState> = {
      selectedLabId: id,
      selectedLabAddress: address,
      selectedTime: time,
    }

    if (isValidTimeZone(timezone)) {
      updateObject.labTimezone = timezone
    }

    setLabState(updateObject)
  }

  // Handle errors from cached query
  useEffect(() => {
    if (cachedTimeSlotsError) {
      const apiError = handleLabSchedulingError(
        cachedTimeSlotsError,
        'Failed to find available locations or appointment slots near you. Please try again.'
      )

      if (
        apiError?.code === LabSchedulingErrorCode.NO_LOCATIONS ||
        apiError?.code === LabSchedulingErrorCode.NO_TIMESLOTS ||
        apiError?.code === LabSchedulingErrorCode.DATE_TOO_FAR
      ) {
        setCriticalErrorCode(apiError.code)
      }
    }
  }, [cachedTimeSlotsError, handleLabSchedulingError])

  // Handle initial validation
  useEffect(() => {
    if (!labState.selectedAddress.length) {
      toast({
        title: 'No address found',
        description:
          'No address was found. Please try inputting an address again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }
  }, [labState.selectedAddress, toast])

  const bookHandler = () => {
    fireSlotSelectedAmplitudeEvent()
    setCurrentScreen('confirm')
  }

  const sortedLocations = useMemo(() => {
    if (sortBy === 'distance') {
      return sortedLocationsByDistance
    }

    return sortedLocationsByFirstAvailable
  }, [sortBy, sortedLocationsByDistance, sortedLocationsByFirstAvailable])

  const renderTimeSlots = () => {
    if (
      !currentTimeSlotsResponse ||
      currentTimeSlotsResponse.locations.length === 0
    ) {
      return (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100%"
          width="100%"
        >
          <Text>Sorry, unable to find any time slots</Text>
        </Flex>
      )
    }

    const { origin } = currentTimeSlotsResponse

    const metadata = {
      labProvider:
        selectedLab === 'LabCorp' ? LabProvider.LABCORP : LabProvider.QUEST,
      labAppointmentId: amplitudeLabSchedulingMetadata.lab_appointment_id ?? '',
      isReschedule: isModifyFlow,
    }

    const filterApplied =
      sortBy === 'distance'
        ? LabSchedulingFilter.DISTANCE_CLOSEST
        : LabSchedulingFilter.APPOINTMENT_FIRST_AVAILABLE

    const mapWidth = '400px'

    return (
      <Flex
        width="100%"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 4 : 0}
        justifyContent="space-around"
      >
        <Flex
          width={isMobile ? '100%' : 'auto'}
          flexDirection="column"
          overflow="auto"
          height={isMobile ? 'auto' : '30rem'}
          maxHeight={isMobile ? '65vh' : 'auto'}
          gap={6}
        >
          {sortedLocations.map((location) => (
            <TimeSlotCard
              key={location.id}
              labLocation={location}
              selectedDate={selectedDate}
              amplitudeLabSchedulingMetadata={metadata}
              selectedSlotData={{
                selectedLabId: labState.selectedLabId,
                selectedTime: labState.selectedTime,
              }}
              filterApplied={filterApplied}
              selectSlotHandler={handleSelectSlot}
              bookHandler={bookHandler}
              setFocus={() => setFocusedLocationId(location.id)}
              isFocused={focusedLocationId === location.id}
              selectedTimezone={selectedTimezone}
              originalTimezone={location.address.timezone || 'America/New_York'}
              isMobile={isMobile}
            />
          ))}
        </Flex>

        {isMobile && (
          <Button
            onClick={() => setIsMapVisible(!isMapVisible)}
            variant="outline"
            size="sm"
            mb={4}
          >
            {isMapVisible ? 'Hide Map' : 'Show Map'}
          </Button>
        )}

        <Box
          width={isMobile ? '100%' : 'auto'}
          paddingLeft={isMobile ? '0' : '1.5rem'}
        >
          <Collapse in={!isMobile || isMapVisible}>
            <Map
              style={{
                width: isMobile ? '100%' : mapWidth,
                height: '400px',
              }}
              defaultCenter={{
                lat: origin.latitude,
                lng: origin.longitude,
              }}
              defaultZoom={12}
              mapId="Lab-Scheduling-Map"
            >
              {sortedLocations.map((location) => {
                const {
                  id,
                  address: { latitude, longitude },
                } = location
                return (
                  <GoogleMarkerWithWindow
                    key={`marker-${id}`}
                    location={location}
                    position={{
                      lat: parseFloat(latitude),
                      lng: parseFloat(longitude),
                    }}
                    setFocus={() => setFocusedLocationId(location.id)}
                    isFocused={focusedLocationId === location.id}
                  />
                )
              })}
            </Map>
          </Collapse>
          <Alert
            backgroundColor="#E8EFF9"
            marginTop="1.5rem"
            color="#2D3748"
            width={isMobile ? '100%' : mapWidth}
          >
            <Image src={alertUrl} marginRight=".675rem" />
            <Flex flexDirection="column">
              <Text
                margin="0"
                fontSize=".875rem"
                fontWeight="500"
                lineHeight="1.5rem"
              >
                Don&apos;t see a time that works for you?
              </Text>
              <Text margin="0" fontSize=".75rem" lineHeight="normal">
                Most Labcorp locations welcome walk-in appointments.
              </Text>
            </Flex>
          </Alert>
        </Box>
      </Flex>
    )
  }

  const renderCriticalError = () => {
    if (!!criticalErrorCode) {
      let alertTitle = ''
      let alertText = ''
      let showButton = false

      if (criticalErrorCode === LabSchedulingErrorCode.NO_LOCATIONS) {
        alertTitle = 'We are unable to find locations near you at this time.'
        alertText =
          'Please reach out to our Patient Concierge for support on completing your lab work.'
        showButton = true
      }

      if (criticalErrorCode === LabSchedulingErrorCode.NO_TIMESLOTS) {
        alertTitle = 'There are no available appointments'
        alertText =
          "We're sorry for the inconvenience, but please check back soon for additional availability"
      }

      if (criticalErrorCode === LabSchedulingErrorCode.DATE_TOO_FAR) {
        alertTitle = 'Date too far in the future'
        alertText =
          'Please select a date within 90 days from today to schedule your appointment.'
      }

      return (
        <Box
          bg="white"
          p={isMobile ? 4 : 8}
          width={isMobile ? '100%' : '4xl'}
          mx={isMobile ? 2 : 0}
          minHeight={isMobile ? '100vh' : 'auto'}
          overflow={isMobile ? 'auto' : 'visible'}
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight={isMobile ? '100%' : 'auto'}
          >
            <Flex
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <Alert
                backgroundColor="#E8EFF9"
                marginTop="1.5rem"
                color="#2D3748"
              >
                <Image
                  src={alertUrl}
                  marginRight=".675rem"
                  alignSelf="flex-start"
                />
                <Flex flexDirection="column">
                  <Text margin="0" fontSize=".875rem" fontWeight="500">
                    {alertTitle}
                  </Text>
                  <Text margin="0" fontSize=".75rem" fontWeight="400">
                    {alertText}
                  </Text>
                </Flex>
              </Alert>
              {showButton && (
                <ChakraPrimaryButton
                  styleProps={{ marginTop: '2.5rem' }}
                  onClick={() =>
                    window.open(RoutePaths.DASHBOARD_NEW_THREAD + '?sbj=8')
                  }
                >
                  Contact Concierge
                </ChakraPrimaryButton>
              )}
            </Flex>
          </Flex>
        </Box>
      )
    }
  }

  return (
    <Box
      bg="white"
      p={isMobile ? 4 : 8}
      borderRadius="lg"
      boxShadow="lg"
      width={isMobile ? '100%' : '5xl'}
      mx={isMobile ? 2 : 0}
      minHeight={isMobile ? '100vh' : 'auto'}
      overflow={isMobile ? 'auto' : 'visible'}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        minHeight={isMobile ? '100%' : 'auto'}
      >
        <Flex
          width="100%"
          justifyContent={isMobile ? 'center' : 'space-between'}
          flexDirection={isMobile ? 'column' : 'row'}
          gap={isMobile ? 4 : 0}
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Text fontFamily="Instrument Serif" fontSize="1.5rem">
              Select date and time
            </Text>
            <MonthCarousel
              selectedMonth={selectedMonth}
              onMonthChange={handleMonthChange}
            />
            <DayCarousel
              allDays={daysToShow}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              visibleDays={isSmallScreen ? 4 : 7}
              availableDays={availableDays}
              previousCacheStartDate={previousCacheStartDateRef.current}
              cacheDayRange={cacheDayRange}
            />
          </Flex>
          {isMobile && (
            <Button
              onClick={() => setAreFiltersVisible(!areFiltersVisible)}
              variant="outline"
              size="sm"
              margin={isMobile ? 2 : 4}
            >
              {areFiltersVisible ? 'Hide Filters' : 'Show Filters'}
            </Button>
          )}
          <Collapse in={!isMobile || areFiltersVisible}>
            <Flex
              alignItems="center"
              justifyContent={isMobile ? 'center' : 'flex-end'}
              gap={2}
              flexDirection="column"
              height="100%"
            >
              <Flex
                alignItems="center"
                alignSelf={isMobile ? 'center' : 'flex-end'}
                flexDirection={isMobile ? 'column' : 'row'}
                gap={isMobile ? 2 : 0}
              >
                <Text
                  fontSize="sm"
                  fontWeight="400"
                  margin="0"
                  whiteSpace="nowrap"
                  ml={isMobile ? 0 : 2}
                  mr={isMobile ? 0 : 2}
                >
                  Time zone:
                </Text>
                <Select
                  id="timezone-select"
                  value={selectedTimezone}
                  onChange={(e) =>
                    handleTimezoneChange(e.target.value as validTimeZones)
                  }
                  size="sm"
                  width="170px"
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </Select>
              </Flex>
              <Flex
                alignItems="center"
                flexDirection={isMobile ? 'column' : 'row'}
                gap={isMobile ? 2 : 0}
                mb={6}
              >
                <Text
                  fontSize="sm"
                  fontWeight="400"
                  margin="0"
                  whiteSpace="nowrap"
                  mr={isMobile ? 0 : 2}
                >
                  Sort:
                </Text>
                <Select
                  id="sort-option-select"
                  value={sortBy}
                  onChange={(e) =>
                    handleSortByChange(
                      e.target.value as 'distance' | 'firstAvailable'
                    )
                  }
                  size="sm"
                  width="170px"
                >
                  <option value="distance">Distance: Closest</option>
                  <option value="firstAvailable">
                    Appointment: First available
                  </option>
                </Select>
                <Text
                  fontSize="sm"
                  fontWeight="400"
                  margin="0"
                  whiteSpace="nowrap"
                  ml={isMobile ? 0 : 2}
                  mr={isMobile ? 0 : 2}
                >
                  Search Radius:
                </Text>
                <Select
                  id="search-radius-select"
                  value={searchRadius}
                  onChange={(e) => {
                    const value = parseInt(e.target.value)

                    if (!isNaN(value)) {
                      handleSearchRadiusChange(value)
                    }
                  }}
                  size="sm"
                  width="170px"
                >
                  {radiusOptions.map((option, idx) => (
                    <option key={`radius-option-${idx}`} value={option}>
                      {option} miles
                    </option>
                  ))}
                </Select>
              </Flex>
            </Flex>
          </Collapse>
        </Flex>
        <Flex
          width="100%"
          padding={isMobile ? 2 : 4}
          flex={1}
          minHeight={isMobile ? 'auto' : '30rem'}
        >
          {isLoadingCachedTimeSlots && (
            <Flex
              justify="center"
              align="center"
              height={isMobile ? '50vh' : '30rem'}
              width="100%"
              minHeight={isMobile ? '300px' : 'auto'}
            >
              <Spinner size="xl" />
            </Flex>
          )}
          {!isLoadingCachedTimeSlots &&
            (!!criticalErrorCode ? renderCriticalError() : renderTimeSlots())}
        </Flex>
        {selectedLab === 'LabCorp' && (
          <Image
            src={LabCorpLogo}
            width="90px"
            marginTop={isMobile ? '1.5rem' : '2.5rem'}
            mb={isMobile ? 4 : 0}
          />
        )}
      </Flex>
    </Box>
  )
}

export default TimeSlotMap
